import { useMemo } from 'react';
import type { EventPageType } from '@graphql/generated-base';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

export type MinimalPage = Readonly<{
  pageSlug: string;
  type: EventPageType;
}>;

type UseDefaultPageReturn<T extends MinimalPage> = Readonly<{
  defaultPage: Readonly<T> | undefined;
  isDefaultPage: (pageSlug: string) => boolean;
  loading: boolean;
}>;

/**
 * Hook that determines the default page for an event.
 *
 * The default page is:
 * 1. The welcome page (if it exists)
 * 2. The first page in the list (if no welcome page)
 *
 * This is used for:
 * - Rendering content at the root route `/:eventHandle/`
 * - Generating navbar links (default page links to root)
 * - Updating URL when scrolling (default page uses root URL)
 */
export const useDefaultPage = <T extends MinimalPage>(pages?: ReadonlyArray<T>): UseDefaultPageReturn<T> => {
  const { checkIsWelcomePage, loading } = useWelcomePage();

  const defaultPage = useMemo(() => {
    if (!pages?.length || loading) {
      return undefined;
    }

    const welcomePage = pages.find(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
    return welcomePage ?? pages[0];
  }, [pages, checkIsWelcomePage, loading]);

  const isDefaultPage = (pageSlug: string) => {
    if (!defaultPage) {
      return false;
    }

    return defaultPage.pageSlug === pageSlug;
  };

  return {
    /** The default page object (welcome page or first page) */
    defaultPage,
    /** Function to check if a given slug is the default page */
    isDefaultPage,
    /** Whether the hook is still loading (waiting for feature flags) */
    loading,
  };
};
